import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Section, showAlert } from '@platform/ui';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import { useAuth } from '@/features/auth';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useInquiryDetail } from '../api/use-inquiry.query.ts';
import { useUpdateInquiry } from '../api/use-inquiry.mutation.ts';
import { InquiryForm } from '../components/InquiryForm.tsx';
import type { CreateInquiryInput } from '../inquiry.type.ts';

/**
 * 문의 수정. 작성자 본인 & 답변 전(PENDING)만 가능.
 * 권한 위반 시 서버가 막지만, FE 에서도 안내 후 상세로 돌려보낸다.
 */
export function InquiryEditView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: inquiry, isLoading, isError, refetch } = useInquiryDetail(id);
  const updateMutation = useUpdateInquiry(id ?? '');

  const handleSubmit = useCallback(
    async (input: CreateInquiryInput) => {
      const updated = await updateMutation.mutateAsync(input);
      navigate(PATHS.inquiryDetail(updated.id), { replace: true });
    },
    [updateMutation, navigate],
  );

  const handleError = useCallback((message: string) => {
    void showAlert({ title: '알림', message, confirmText: '확인' });
  }, []);

  if (isLoading) {
    return <StateBox>불러오는 중…</StateBox>;
  }
  if (isError || !inquiry) {
    return (
      <StateBox>
        문의를 불러오지 못했습니다.{' '}
        <button type="button" onClick={() => refetch()} className="font-semibold underline">
          다시 시도
        </button>
      </StateBox>
    );
  }

  const isOwner = !!user?.id && user.id === inquiry.authorId;
  const editable = isOwner && inquiry.status === 'PENDING';
  if (!editable) {
    return (
      <StateBox>
        수정할 수 없는 문의입니다.
        <div className="mt-4">
          <button
            type="button"
            onClick={() => navigate(PATHS.inquiryDetail(inquiry.id))}
            className="text-[13px] font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-950 dark:hover:text-white"
          >
            ← 상세로
          </button>
        </div>
      </StateBox>
    );
  }

  const seo = buildSeo({
    title: '문의 수정',
    description: '문의 내용을 수정합니다.',
    path: PATHS.inquiryEdit(inquiry.id),
  });

  return (
    <>
      <SEO {...seo} />
      <main className="bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <Section spacing="default">
          <Container>
            <div className="mx-auto max-w-3xl pt-[100px] sm:pt-28">
              <header className="mb-8 border-b border-black/10 dark:border-white/10 pb-5">
                <h1 className="text-3xl font-extrabold -tracking-[0.03em]">
                  문의 수정
                </h1>
              </header>
              <InquiryForm
                initial={inquiry}
                onSubmit={handleSubmit}
                submitting={updateMutation.isPending}
                onCancel={() => navigate(PATHS.inquiryDetail(inquiry.id))}
                onError={handleError}
              />
            </div>
          </Container>
        </Section>
      </main>
    </>
  );
}

function StateBox({ children }: { children: React.ReactNode }) {
  return <main className="bg-white dark:bg-dark-base">
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-5 pt-28 text-center text-[14px] text-neutral-950/55 dark:text-white/55">
      {children}
    </div>
  </main>
}
